import React, { useRef, useState } from 'react';
import { addDays, subDays } from 'date-fns';
import { styled } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { Colors } from 'library';
import {
    DatePicker,
    Divider,
    IconButton,
    Label,
    Link,
    Modal,
    PillButton,
    RadioGroup,
    Row,
    Stack,
    Text,
    Title,
} from 'library/ui';
import { client, consoleLogInDev } from 'shared';
import { ShiftProviderUser } from 'models';
import RevealModal from 'parent-portal/jobs/upcoming/BusinessCalendar/ShiftCard/RevealModal';

const CLEARINGHOUSE_URL = 'https://crw.flclearinghouse.com/';

export type BookResult = {
    status: 'booked' | 'hiring_group' | 'unavailable';
    reason?: 'last_minute' | 'stale_application';
};

export type VerificationForm = {
    eligibility_date: string;
    expiration_date: string;
    screening_document?: File;
};

export function BookResultModal({
    workerName,
    result,
    onClose,
}: {
    workerName: string;
    result: BookResult;
    onClose: () => void;
}) {
    const titles = {
        booked: 'Added to Schedule',
        hiring_group: 'Confirmation Needed',
        unavailable: 'No Longer Available',
    };
    const messages = {
        booked: `${workerName} has been added to your schedule successfully!`,
        hiring_group:
            result.reason === 'last_minute'
                ? `Because this shift is last minute, we need ${workerName} to confirm they can make it. We've sent them a request to accept and will add them to your schedule as soon as they do.`
                : `Because ${workerName} applied more than a week ago, we need them to confirm they're still available. We've sent them a request to accept and will add them to your schedule as soon as they do.`,
        unavailable: `Unfortunately, ${workerName} is no longer available for these shifts.`,
    };
    return (
        <Modal title={titles[result.status]} onClose={onClose} width={440}>
            <Stack gap="md">
                <Text>{messages[result.status]}</Text>
                <Row justify="end" fullWidth>
                    <PillButton onClick={onClose}>Done</PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}

const LabeledField = ({
    label,
    help,
    image,
    required,
    children,
}: {
    label: string;
    help: React.ReactNode;
    image?: string;
    required?: boolean;
    children: React.ReactNode;
}) => {
    const [helpOpen, setHelpOpen] = useState(false);
    return (
        <Stack gap="sm">
            <Row gap="sm" align="center">
                <Label>
                    {label}
                    {required && (
                        <Text component="span" tone="error">
                            {' '}
                            *
                        </Text>
                    )}
                </Label>
                <IconButton
                    icon={HelpOutline}
                    onClick={() => setHelpOpen(true)}
                    tooltip="How do I find this?"
                    size={24}
                    iconSize={16}
                />
            </Row>
            {children}
            {helpOpen && (
                <Modal title={`How to find: ${label}`} onClose={() => setHelpOpen(false)} width={760}>
                    <Stack gap="md">
                        {typeof help === 'string' ? <Text tone="secondary">{help}</Text> : help}
                        <ScreenshotFrame>
                            {image ? <ScreenshotImg src={image} alt={label} /> : 'Screenshot goes here'}
                        </ScreenshotFrame>
                    </Stack>
                </Modal>
            )}
        </Stack>
    );
};

const ScreenshotFrame = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 220,
    borderRadius: 10,
    border: `1px dashed ${Colors.border}`,
    backgroundColor: Colors.surface,
    color: Colors.textSecondary,
    overflow: 'hidden',
});

const ScreenshotImg = styled('img')({
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
});

export default function VerifyEligibilityModal({
    workerId,
    workerName,
    submitVerification,
    onClose,
    submitLabel = 'Book',
}: {
    workerId: number;
    workerName: string;
    submitVerification: (form: VerificationForm) => Promise<BookResult | null>;
    onClose: (result?: BookResult) => void;
    submitLabel?: string;
}) {
    const [revealOpen, setRevealOpen] = useState(false);
    const [eligible, setEligible] = useState<string | null>(null);
    const [determinationDate, setDeterminationDate] = useState('');
    const [printsExpirationDate, setPrintsExpirationDate] = useState('');
    const [clearinghouseRecord, setClearinghouseRecord] = useState<File>();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<BookResult>();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [firstName, ...lastNameParts] = workerName.split(' ');
    const provider = {
        id: workerId,
        first_name: firstName,
        last_name: lastNameParts.join(' '),
    } as ShiftProviderUser;

    function onRevealInfo() {
        setRevealOpen(true);
        client(`api/business/reveal-code/`, { method: 'POST', body: { provider: provider.id } }).catch(() => {
            alert('Cannot send SSN reveal code');
        });
    }

    function book() {
        setLoading(true);
        submitVerification({
            eligibility_date: determinationDate,
            expiration_date: printsExpirationDate,
            screening_document: clearinghouseRecord,
        })
            .then((bookResult) => {
                if (bookResult) {
                    setResult(bookResult);
                } else {
                    onClose();
                }
            })
            .catch(consoleLogInDev)
            .finally(() => setLoading(false));
    }

    if (result) {
        return <BookResultModal workerName={workerName} result={result} onClose={() => onClose(result)} />;
    }

    return (
        <Modal title={`Verify ${workerName}'s Eligibility to Book`} onClose={() => onClose()} width={560}>
            <Stack gap="md">
                <Stack gap="sm">
                    <Title size="h2">1. Check the Clearinghouse</Title>
                    <Text tone="secondary">
                        Use the information below to look up {workerName} in the DCF Background Screening{' '}
                        <Link href={CLEARINGHOUSE_URL}>Clearinghouse</Link>. Confirm that their status shows they are
                        eligible for child care.
                    </Text>
                    <Row>
                        <PillButton variant="secondary" onClick={onRevealInfo}>
                            View Worker Information
                        </PillButton>
                    </Row>
                </Stack>
                <RevealModal open={revealOpen} onClose={() => setRevealOpen(false)} provider={provider} minimal />
                <Divider />
                <Title size="h2">2. Confirm Eligibility</Title>
                <LabeledField
                    label={`Is ${workerName} eligible for DCF Child Care?`}
                    help="On the worker's Clearinghouse record, check that their DCF Child Care status shows they are eligible. If it does not say eligible, they are not eligible for DCF Child Care."
                    image="https://cdn.join-tandem.com/media/tandem_static/child_care_eligibility_instructions.png"
                    required
                >
                    <RadioGroup
                        options={[
                            { value: 'yes', label: 'Yes' },
                            { value: 'no', label: 'No' },
                        ]}
                        value={eligible}
                        onChange={setEligible}
                    />
                </LabeledField>
                {eligible === 'no' && (
                    <Text tone="error">
                        Unfortunately, you cannot book this substitute right now because they are not eligible for DCF
                        Child Care.
                    </Text>
                )}
                {eligible === 'yes' && (
                    <Stack gap="md">
                        <LabeledField
                            label="DCF Child Care Eligibility Determination Date"
                            help="Find this on the worker's Clearinghouse record for DCF Child Care, then input the date."
                            image="https://cdn.join-tandem.com/media/tandem_static/child_care_eligibility_date_instructions.png"
                            required
                        >
                            <DatePicker
                                value={determinationDate}
                                onChange={setDeterminationDate}
                                maxDate={subDays(new Date(), 1)}
                                fullWidth
                            />
                        </LabeledField>
                        <LabeledField
                            label="Retained Prints Expiration Date"
                            help="Find the Retained Prints Expiration Date on the worker's Clearinghouse record in the top right corner, then input the date."
                            image="https://cdn.join-tandem.com/media/tandem_static/retained_prints_instructions.png"
                            required
                        >
                            <DatePicker
                                value={printsExpirationDate}
                                onChange={setPrintsExpirationDate}
                                minDate={addDays(new Date(), 1)}
                                fullWidth
                            />
                        </LabeledField>
                        <LabeledField
                            label="Clearinghouse Record"
                            help={
                                <Stack gap="sm">
                                    <Text tone="secondary">
                                        Press Print in the top-right corner of the worker&rsquo;s Clearinghouse record,
                                        then save it as a PDF and upload the file here.
                                    </Text>
                                    <Text tone="secondary">
                                        <b>On a Mac:</b> in the print window, choose Save to Preview (or open the PDF
                                        dropdown and pick Save as PDF), then save the file.
                                    </Text>
                                    <Text tone="secondary">
                                        <b>On Windows:</b> in the print window, set the printer to Microsoft Print to
                                        PDF (or Save as PDF), then click Print and save the file.
                                    </Text>
                                </Stack>
                            }
                            image="https://cdn.join-tandem.com/media/tandem_static/print_results_instructions.png"
                        >
                            <Row gap="sm">
                                <PillButton variant="secondary" onClick={() => fileInputRef.current?.click()}>
                                    {clearinghouseRecord ? 'Replace file' : 'Upload file'}
                                </PillButton>
                                {clearinghouseRecord && <Text tone="secondary">{clearinghouseRecord.name}</Text>}
                            </Row>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg"
                                style={{ display: 'none' }}
                                onChange={(e) => setClearinghouseRecord(e.target.files?.[0])}
                            />
                        </LabeledField>
                    </Stack>
                )}
                <Divider />
                <Text tone="secondary">
                    Once you confirm that {workerName} is eligible, we&rsquo;ll add them to your schedule. If they go 90
                    days without working at your center, you&rsquo;ll need to verify their eligibility again.
                </Text>
                <Row justify="end" fullWidth>
                    <PillButton
                        disabled={eligible !== 'yes' || !determinationDate || !printsExpirationDate || loading}
                        onClick={book}
                    >
                        {loading ? 'Submitting...' : submitLabel}
                    </PillButton>
                </Row>
            </Stack>
        </Modal>
    );
}
